<template>
    <div class="calendar-filters" role="region" aria-label="Event filters">
        <h2 class="calendar-filters__title">Filter by</h2>
        <div class="calendar-filters__container">
            <template
                v-for="(filter, index) in filters"
                :key="index"
            >
                <details
                    v-if="filter.display && filter.facets.length > 0"
                    class="calendar-filters__fieldset"
                    :open="filter.isOpen"
                >
                    <summary class="calendar-filters__summary">
                        {{ filter.title }}
                    </summary>
                    <fieldset class="calendar-filters__group">
                        <legend class="visually-hidden">Filter by {{ filter.title }}</legend>
                        <div class="calendar-filters__options">
                            <label v-for="facet in filter.facets" :key="facet.title" class="calendar-filters__label form-label--checkbox"
                                :class="{ 'is-disabled': facet.isDisabled }">
                                <input type="checkbox" :checked="facet.isChecked" :disabled="facet.isDisabled"
                                    @change="updateFacet(filter, facet)" class="calendar-filters__checkbox form-input--checkbox" />
                                <span>{{ facet.title }}</span>
                            </label>
                        </div>
                    </fieldset>
                </details>
            </template>
        </div>
        <button @click="viewResults" class="calendar-filters__view-results">
            View {{ displayCount }} results
        </button>
        <button v-if="canReset" @click="resetFilters" class="calendar-filters__reset">
            Reset
        </button>
    </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, watch, computed } from 'vue';
import { flatten, uniqBy, get, compact, map, debounce } from 'lodash';
import { useState } from '../services/stateful';
import { isSchoolBooking } from '../../services/performance';
import Fuse from 'fuse.js';

const emit = defineEmits(['close', 'update:filteredEvents']);

const props = defineProps({
    events: {
        type: Array,
        required: true,
    },
});

const filteredEvents = useState('Calendar.FilteredEvents', []);

const FILTER_CONFIG = [
    {
        name: 'type',
        display: true,
        title: 'Event Type',
        field: 'attributes.custom.performance_type',
        facets: [],
        isOpen: false,
    },
    {
        name: 'performanceType',
        display: false, // Only used as part of URL filtering
        title: 'School',
        field: 'performanceType',
        facets: [],
        isOpen: false,
    },
    {
        name: 'accessibility',
        display: true, // Only used as part of URL filtering
        title: 'Patron & Accessibility Services',
        field: 'accessibility',
        facets: [],
        isOpen: false,
    },
];

const filters = ref([]);
const displayCount = ref(0);

let allPerformances = null;
let fuse = null;
let filteredPerformances = null;
let animationId = null;

const prefersReducedMotion = () => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

const buildQuery = (appliedFacets) => {
    return compact(
        map(appliedFacets, (facets, filter) => {
            if (!facets.length) return null;

            return {
                '$and': facets.map(facet => ({
                    [filter]: facet,
                })),
            };
        })
    );
}

const searchParams = new URLSearchParams(document.location.search);
if (searchParams.get('performanceType') !== 'school') {
    // Defaults to public if not school
    searchParams.set('performanceType', 'public');
}
const isSchool = searchParams.get('performanceType') === 'school';

const setupFilters = (performances) => {
    return FILTER_CONFIG.map(filter => {
        let facets = performances.map(item => {
            if (filter.name === 'type') {
                return item.attributes?.custom?.performance_type ?
                    [item.attributes.custom.performance_type] :
                    [];
            }
            if (filter.name === 'performanceType') {
                return [item.performanceType];
            }

            const value = get(item, filter.field);
            return Array.isArray(value) ? value : (value ? [value] : []);
        });

        facets = flatten(facets);
        facets = uniqBy(facets, facet => facet);

        const existing = searchParams.getAll(filter.name);
        filter.facets = facets.map(facet => ({
            title: facet,
            isChecked: existing.includes(facet),
            isDisabled: false,
        }));

        // Opens the filter if any items are checked
        if (filter.facets.find(f => f.isChecked)) {
            filter.isOpen = true;
        }

        return filter;
    });
}

const canReset = computed(() => {
    return filters.value.some(filter =>
        filter.facets.some(facet => facet.isChecked)
    );
});

const processResults = () => {
    const appliedFacets = {};

    for (const filter of filters.value) {
        appliedFacets[filter.field] = [];

        for (const facet of filter.facets) {
            if (facet.isChecked) {
                appliedFacets[filter.field].push(facet.title);
            }
        }
    }

    const appliedQuery = buildQuery(appliedFacets);

    if (appliedQuery.length) {
        filteredPerformances = fuse.search({
            '$and': appliedQuery,
        }).map(result => result.item);
        canReset.value = true;
    } else {
        filteredPerformances = allPerformances;
        canReset.value = false;
    }

    const availableValues = {};
    filters.value.forEach(filter => {

        availableValues[filter.field] = new Set(
            filteredPerformances.flatMap(item => {
                const value = get(item, filter.field);

                if (Array.isArray(value)) {
                    return value;
                } else if (value !== undefined && value !== null) {
                    return [value];
                }
                return [];
            })
        );
    });

    filters.value = filters.value.map(filter => {
        filter.facets = filter.facets.map(facet => {
            if (!facet.isChecked) {
                facet.isDisabled = !availableValues[filter.field].has(facet.title);
            }

            return facet;
        });

        return filter;
    });

    animateCount(filteredPerformances ? filteredPerformances.length : props.events.length);

    filteredEvents.value = filteredPerformances;
};

const debouncedProcessResults = debounce(() => {
    processResults();
}, 150);

const animateCount = (targetCount) => {
    if (prefersReducedMotion()) {
        displayCount.value = targetCount;
        return;
    }

    if (animationId) {
        cancelAnimationFrame(animationId)
    }

    const startCount = displayCount.value;
    const difference = targetCount - startCount;
    const duration = 300; // ms
    const startTime = performance.now();

    const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        displayCount.value = Math.round(startCount + difference * progress);

        if (progress < 1) {
            animationId = requestAnimationFrame(animate);
        }
    };

    animationId = requestAnimationFrame(animate);
};

const viewResults = () => {
    emit('close');
};

const updateFacet = (filter, facet) => {
    facet.isChecked = !facet.isChecked;
    const url = new URL(document.location);

    if (facet.isChecked) {
        url.searchParams.append(filter.name, facet.title);
    } else {
        url.searchParams.delete(filter.name, facet.title);
    }

    history.pushState({}, '', url);
    debouncedProcessResults();
};

const resetFilters = () => {
    filters.value = filters.value.map(filter => {
        filter.facets = filter.facets.map(facet => {
            facet.isChecked = false;
            return facet;
        });
        return filter;
    });

    processResults();
};

watch(() => props.events, (newEvents, oldEvents) => {
    if (newEvents.length === 0) return;
    if (newEvents === oldEvents) return;

    allPerformances = newEvents.filter(perf => isSchool === isSchoolBooking(perf));

    fuse = new Fuse(allPerformances, {
        threshold: 0.3,
        useExtendedSearch: true,
        includeScore: false,
        shouldSort: false,
        keys: [
            'event.title',
            'event.attributes.custom.genres',
            'attributes.custom.performance_type',
            'performanceType',
            'accessibility',
        ],
    });

    filters.value = setupFilters(allPerformances);
    displayCount.value = allPerformances.length;
    processResults();
}, { immediate: true });

</script>
